import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import style from "./style.module.css";
import { EntAnimalPepublicResponse, EntRegionAnimals, EntAnimalInfo, EntAnimalByRegionItem } from '../api/types';
import { ResponseDataGet } from '../../../../models/responseDataGet';

interface RegionTableProps {
    data?: ResponseDataGet<EntAnimalPepublicResponse> | ResponseDataGet<EntAnimalByRegionItem>;
    isLoading?: boolean;
    selectedRegionName?: string;
}

interface RegionWithExpanded extends EntRegionAnimals {
    expanded?: boolean;
}

interface RegionByItemWithExpanded extends EntAnimalByRegionItem {
    expanded?: boolean;
}

export const RegionTable: React.FC<RegionTableProps> = ({ data, isLoading, selectedRegionName }) => {
    const { t } = useTranslation();
    const [regionsData, setRegionsData] = useState<Array<RegionWithExpanded | RegionByItemWithExpanded>>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    React.useEffect(() => {
        console.log("API data для региональной таблицы:", data);
        
        if (!data || !data.res) {
            setRegionsData([]);
            setTotalCount(0);
            return;
        }

        if ('data' in data.res && Array.isArray(data.res.data)) {
            const initialRegions = data.res.data.map(region => ({
                ...region,
                expanded: false
            })) as RegionWithExpanded[];
            
            setRegionsData(initialRegions);
            setTotalCount('total' in data.res ? Number(data.res.total) || 0 : 0);
        } 
        else if (Array.isArray(data.res.content)) {
            
            const initialRegions = data.res.content.map(item => ({
                ...item,
                expanded: false
            })) as RegionByItemWithExpanded[];
            
            setRegionsData(initialRegions);
            const total = initialRegions.reduce((sum, item) => sum + (('amount' in item) ? Number(item.amount) || 0 : 0), 0);
            setTotalCount(total);
        } else {
            setRegionsData([]);
            setTotalCount(0);
        }
    }, [data]);

    const toggleRegion = (index: number) => {
        const updatedRegions = [...regionsData];
        updatedRegions[index].expanded = !updatedRegions[index].expanded;
        setRegionsData(updatedRegions);
    };

    return (
        <div className={style.tableContainer}>
            {selectedRegionName && (
                <div className={style.selectedRegionTitle}>
                    {t('Данные по области')}: <b>{selectedRegionName}</b>
                </div>
            )}
            <h3 className={style.tableTitle}>{t('Данные по регионам')}</h3>
            <p className={style.totalCount}>{t('Всего животных')}: {totalCount.toLocaleString()}</p>

            <table className={style.regionTable}>
                <thead>
                    <tr>
                        <th className={style.areaColumn}>{t('Регион')}</th>
                        <th className={style.countColumn}>{t('Количество')}</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={2} style={{ textAlign: 'center', padding: '20px' }}>
                                {t('Загрузка...')}
                            </td>
                        </tr>
                    ) : regionsData && regionsData.length > 0 ? (
                        regionsData.map((region, index) => (
                            <React.Fragment key={`region-group-${index}`}>
                                <tr className={style.regionRow}>
                                    <td className={style.regionCell}>
                                        <span
                                            className={style.expandButton}
                                            onClick={() => toggleRegion(index)}
                                        >
                                            {region.expanded ? '−' : '+'}
                                        </span>
                                        {region.nameRu }
                                    </td>
                                    <td className={style.countCell}>
                                        {('amount' in region) ? region.amount.toLocaleString() : '0'}
                                        {('percentage' in region) && region.percentage !== undefined && (
                                            <span style={{ color: '#888', fontSize: '12px' }}>
                                                ({region.percentage.toFixed(1)}%)
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                {region.expanded && 'animals' in region && region.animals?.map((animal, subIndex) => (
                                    <tr key={`subitem-${index}-${subIndex}`} className={style.subItemRow}>
                                        <td className={style.subItemCell}>
                                            {('animalNameRu' in animal) ? animal.animalNameRu : ''} 
                                        </td>
                                        <td className={style.countCell}>
                                            {('totalHeadCount' in animal) ? animal.totalHeadCount.toLocaleString() : '0'}
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={2} style={{ textAlign: 'center', padding: '20px' }}>
                                {t('Данных нет')}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}